import { useEffect, useState } from "react";
import { useAuth } from "../auth/useAuth";
import { fetchMyWaitlistStatus, type WaitlistStatus } from "./index";

const E2E_BYPASS = import.meta.env.VITE_E2E_BYPASS_AUTH === "true";

export interface WaitlistStatusState {
  status: WaitlistStatus | null;
  loading: boolean;
  error: Error | null;
  refetch(): void;
}

/**
 * Reads the signed-in user's waitlist status via `get_my_waitlist_status`
 * RPC. Re-fetches whenever the user id changes (sign-in / sign-out).
 *
 * Under VITE_E2E_BYPASS_AUTH the RPC is not reachable, so we short-circuit
 * to `allowlisted` — this keeps the existing Playwright suite green without
 * stubbing Supabase.
 */
export function useWaitlistStatus(): WaitlistStatusState {
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState<WaitlistStatus | null>(
    E2E_BYPASS ? "allowlisted" : null,
  );
  const [loading, setLoading] = useState<boolean>(!E2E_BYPASS);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (E2E_BYPASS) {
      setStatus("allowlisted");
      setLoading(false);
      return;
    }
    if (authLoading) return;
    if (!user) {
      setStatus(null);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    fetchMyWaitlistStatus()
      .then((s) => {
        if (!active) return;
        setStatus(s);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!active) return;
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        // Fail closed: treat as pending so we don't accidentally drop a
        // non-allowlisted user into the app on a transient RPC failure.
        setStatus("pending");
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [user?.id, authLoading, tick]);

  return {
    status,
    loading,
    error,
    refetch: () => setTick((n) => n + 1),
  };
}
